import React from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

export type RefreshIconButtonProps = {
  /** May return a value (e.g. fetch helpers); the button ignores the result. */
  onClick: () => unknown | Promise<unknown>;
  disabled?: boolean;
  /** Shows a spinner inside the button */
  loading?: boolean;
  /** Tooltip text and `aria-label` */
  title?: string;
  size?: 'small' | 'medium' | 'large';
  color?: 'default' | 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
};

/**
 * Standard refresh control (icon + tooltip + aria-label).
 * Prefer this over mixing plain IconButtons and outlined "Refresh" text buttons in headers.
 */
export function RefreshIconButton({
  onClick,
  disabled = false,
  loading = false,
  title = 'Refresh',
  size = 'medium',
  color = 'default',
}: RefreshIconButtonProps) {
  const busy = Boolean(loading);
  const spinnerSize = size === 'small' ? 18 : 22;
  return (
    <Tooltip title={busy ? `${title}…` : title}>
      <span>
        <IconButton
          onClick={() => void onClick()}
          disabled={disabled || busy}
          aria-label={title}
          aria-busy={busy}
          size={size}
          color={color}
        >
          {busy ? <CircularProgress color="inherit" size={spinnerSize} /> : <RefreshIcon />}
        </IconButton>
      </span>
    </Tooltip>
  );
}
